import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MergeTypeIcon from "@mui/icons-material/MergeType";

import AccountDetailCard from "./AccountDetailCard";
import MergeAllDuplicatesDialog from "./MergeAllDuplicatesDialog";

/** Coerce API row ids so recommended keeper and selection compare cleanly. */
const normalizeId = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const labelForAccount = (acc, entityType) => {
  if (!acc) return "Account";
  if (entityType === "users") {
    const fullName = [acc.first_name, acc.last_name].filter(Boolean).join(" ");
    return fullName || acc.username || acc.email || `User ${acc.user_id}`;
  }
  if (entityType === "law_firms") {
    return acc.name || `Law firm ${acc.lawfirm_id}`;
  }
  return acc.full_name || acc.business_name || `PG ${acc.pg_id}`;
};

/**
 * One expandable row in a duplicate-groups table.
 *
 * Flow
 *  1. Click any active card → it becomes the keeper (recommended is preselected).
 *  2. The keeper card shows a single button: "Merge N duplicates into this account".
 *  3. Click → confirm → bulk-merge runs (one transaction per pair) → list refreshes.
 *  4. Soft-deleted cards remain visible but disabled; they are not keeper- or
 *     merge-eligible.
 *
 * Groups with fewer than two active accounts are filtered out by the parent
 * table — there is nothing left to merge.
 */
const DuplicateGroupRow = ({
  group,
  entityType,
  isSelected,
  onSelectionChange,
  onMerged,
}) => {
  const [open, setOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  const idField =
    entityType === "users"
      ? "user_id"
      : entityType === "law_firms"
      ? "lawfirm_id"
      : "pg_id";

  const accounts = group.accounts;

  /** Active = not soft-deleted. Only active accounts can be keeper / loser. */
  const activeAccountIds = useMemo(
    () =>
      accounts
        .filter((a) => !a.deleted_at)
        .map((a) => normalizeId(a[idField]))
        .filter((id) => id != null),
    [accounts, idField]
  );

  /** Default keeper = recommended (only if active), else the first active account. */
  const defaultKeeperId = useMemo(() => {
    const rec = normalizeId(group.recommended_keeper_id);
    if (rec != null && activeAccountIds.includes(rec)) return rec;
    return activeAccountIds[0] ?? null;
  }, [activeAccountIds, group.recommended_keeper_id]);

  const [keeperId, setKeeperId] = useState(defaultKeeperId);

  useEffect(() => {
    setKeeperId(defaultKeeperId);
  }, [defaultKeeperId, group.key]);

  const loserIds = useMemo(
    () => activeAccountIds.filter((id) => id !== keeperId),
    [activeAccountIds, keeperId]
  );

  const keeperAccount = useMemo(
    () => accounts.find((a) => normalizeId(a[idField]) === keeperId) || null,
    [accounts, idField, keeperId]
  );

  const duplicateDescriptors = useMemo(
    () =>
      loserIds.map((lid) => {
        const acc = accounts.find((a) => normalizeId(a[idField]) === lid);
        return { id: lid, label: labelForAccount(acc, entityType) };
      }),
    [accounts, idField, entityType, loserIds]
  );

  const handleKeeperChange = (newKeeperId) => {
    const kid = normalizeId(newKeeperId);
    if (kid == null) return;
    if (!activeAccountIds.includes(kid)) return; // safety: a soft-deleted card cannot become keeper
    setKeeperId(kid);
    if (isSelected && onSelectionChange) {
      onSelectionChange(group.key, kid, activeAccountIds.filter((id) => id !== kid));
    }
  };

  const handleCheckbox = (e) => {
    if (!onSelectionChange) return;
    if (e.target.checked) {
      onSelectionChange(group.key, keeperId, loserIds);
    } else {
      onSelectionChange(group.key, null, []);
    }
  };

  const handleBulkMerged = (result) => {
    // Only refresh the list if at least one merge succeeded.
    if (result?.success_count > 0 && onMerged) {
      onMerged({ message: `Merged ${result.success_count} duplicate(s) into keeper.` });
    }
  };

  const summaryTitle = group.key || "(empty)";
  const totalActive = activeAccountIds.length;
  const totalSoftDeleted = accounts.length - totalActive;

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={!!isSelected}
            onChange={handleCheckbox}
            disabled={loserIds.length === 0}
          />
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{summaryTitle}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {totalActive} active account{totalActive === 1 ? "" : "s"}
              {totalSoftDeleted > 0 ? ` · ${totalSoftDeleted} already removed` : ""}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          {keeperId != null ? (
            <Chip size="small" label={`Keeper: ${keeperId}`} color="primary" variant="outlined" />
          ) : (
            <Chip size="small" label="No active keeper" color="default" variant="outlined" />
          )}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={`${loserIds.length} duplicate${loserIds.length === 1 ? "" : "s"}`}
            color="warning"
            variant="outlined"
          />
        </TableCell>
        <TableCell align="right">
          <Button
            size="small"
            variant="outlined"
            startIcon={<MergeTypeIcon fontSize="small" />}
            disabled={loserIds.length === 0 || keeperId == null}
            onClick={() => setOpen(true)}
          >
            {open ? "Review" : "Review & merge"}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, backgroundColor: "rgba(37,99,235,0.04)", borderRadius: 1, mb: 1 }}>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Click an active card to choose the keeper, then use the merge button on the
                  keeper card.
                </Typography>
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  All bond and profile data from every duplicate is moved into the keeper, then
                  each duplicate is soft-deleted. Soft-deleted accounts shown below are already
                  removed by a previous merge and cannot be selected.
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                {accounts.map((acc) => {
                  const accId = normalizeId(acc[idField]);
                  const isKeeper = keeperId != null && accId === keeperId;
                  return (
                    <Grid size={{ xs: 12, md: 6 }} key={accId ?? acc[idField]}>
                      <AccountDetailCard
                        account={acc}
                        entityType={entityType}
                        isKeeper={isKeeper}
                        isRecommended={accId === normalizeId(group.recommended_keeper_id)}
                        duplicateCount={duplicateDescriptors.length}
                        onSelectAsKeeper={handleKeeperChange}
                        onMergeAllIntoKeeper={() => setMergeDialogOpen(true)}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <MergeAllDuplicatesDialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        mergeType={
          entityType === "users"
            ? "portal_user"
            : entityType === "law_firms"
            ? "law_firm"
            : "professional_guardian"
        }
        keeperId={keeperId}
        keeperLabel={keeperAccount ? `${labelForAccount(keeperAccount, entityType)} — ID ${keeperId}` : undefined}
        duplicates={duplicateDescriptors}
        onMerged={handleBulkMerged}
      />
    </>
  );
};

DuplicateGroupRow.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string,
    accounts: PropTypes.array,
    recommended_keeper_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityType: PropTypes.oneOf(["users", "law_firms", "pgs"]).isRequired,
  isSelected: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onMerged: PropTypes.func,
};

export default DuplicateGroupRow;
